import React, { useEffect, useState } from "react";
import Link from "@mui/material/Link";
import {
  Container,
  Typography,
  Paper,
  Box,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Divider,
  Card,
  CardContent,
} from "@mui/material";
import {
  Send,
  Email,
  Person,
  Subject,
  Feedback,
  Info,
  Warning,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { submitFeedback, clearFeedbackState } from "../store/slices/feedbackSlice";

const ContactPage = () => {
  const dispatch = useDispatch();
  const { loading, success, error } = useSelector((state) => state.feedback);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
    feedbackType: "DATA_CORRECTION",
  });

  const [errors, setErrors] = useState({});
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const feedbackTypes = [
    { value: "DATA_CORRECTION", label: "Корекция на данни", icon: "📊" },
    { value: "TECHNICAL_ISSUE", label: "Технически проблем", icon: "🛠️" },
    { value: "SUGGESTION", label: "Предложение за подобрение", icon: "💡" },
    { value: "GENERAL_QUESTION", label: "Общ въпрос", icon: "❓" },
    { value: "OTHER", label: "Друго", icon: "📝" },
  ];

   useEffect(() => {
    if (success) {
      handleReset();
      setShowSuccessMessage(true);
      
      const timer = setTimeout(() => {
        setShowSuccessMessage(false);
        dispatch(clearFeedbackState());
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [success, dispatch]);


  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Моля, въведете вашето име";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Моля, въведете вашия имейл";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Моля, въведете валиден имейл адрес";
    }

    if (!formData.subject.trim()) {
      newErrors.subject = "Моля, въведете тема";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Моля, въведете вашето съобщение";
    } else if (formData.message.length < 10) {
      newErrors.message = "Съобщението трябва да бъде поне 10 символа";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    dispatch(submitFeedback(formData));
  };

  const handleReset = () => {
    setFormData({
      name: "",
      email: "",
      subject: "",
      message: "",
      feedbackType: "DATA_CORRECTION",
    });
    setErrors({});
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 4 }}>
            {/* Информационен текст */}
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, mb: 3, color: "primary.main" }}
              >
                Версия 1.0 на Общинския барометър на читалищата
              </Typography>

              <Typography
                paragraph
                sx={{ mb: 3, lineHeight: 1.7, textAlign: "justify" }}
              >
                Въпреки положените усилия за проверка и обработка на данните, е
                възможно да има неточности или непълна информация. Всички данни
                в картата са извлечени от данните в Публичния регистър на
                народните читалища и на читалищните сдружения (Министерството на
                културата), както и на данните, предоставени от Министерство на
                финансите, Националния статистически институт, и Националната
                агенция по приходите.
              </Typography>

              <Card
                variant="outlined"
                sx={{
                  mb: 4,
                  borderColor: "warning.main",
                  backgroundColor: "rgba(255, 193, 7, 0.05)",
                }}
              >
                <CardContent>
                  <Box
                    sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}
                  >
                    <Warning color="warning" />
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Важно:
                      </Typography>
                      <Typography variant="body1" sx={{ textAlign: "justify" }}>
                        Затова ви молим при установяване на липси или грешки
                        първо да проверите съответните данни в таблиците
                        предоставени от институции - може да ги намерите в
                        раздела „База данни".
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              <Typography
                paragraph
                sx={{ lineHeight: 1.7, textAlign: "justify" }}
              >
                Насърчаваме обратната връзка от граждани, читалища, общини и
                други институции и организации, които използват картата. Ако
                забележите грешна информация или неточности, можете да ни
                сигнализирате чрез този раздел. Вашите сигнали ще ни помогнат
                постепенно да подобряваме качеството на данните и да развиваме
                Барометъра като споделен и полезен ресурс за всички.
              </Typography>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
              <Feedback color="primary" sx={{ fontSize: 36 }} />
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Можете да изпратите вашите сигнали и предложения за подобрение
                на имейл:{" "}
                <Link
                  href="mailto:info@narodnichtalishta.bg"
                  sx={{
                    color: "#f57c00", // Оранжев цвят
                    textDecoration: "none", // Без подчертаване по подразбиране
                    "&:hover": {
                      textDecoration: "underline", // Подчертаване само при ховър
                      textDecorationColor: "#f57c00", // Оранжево подчертаване (незадължително)
                    },
                  }}
                >
                  info@narodnichitalishta.bg
                </Link>
              </Typography>
            </Box> */}

            {/* Заглавие на формата */}
            {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
              <Feedback color="primary" sx={{ fontSize: 36 }} />
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Форма за обратна връзка
              </Typography>
            </Box> */}

            {/* Съобщения за успех/грешка */}
            {showSuccessMessage && (
              <Alert
                severity="success"
                sx={{ mb: 3 }}
                icon={<Info fontSize="inherit" />}
              >
                <Typography sx={{ fontWeight: 500 }}>
                  Благодарим ви за обратната връзка!
                </Typography>
                <Typography variant="body2">
                  Вашето съобщение беше изпратено успешно. Ще се свържем с вас
                  при необходимост.
                </Typography>
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                <Typography sx={{ fontWeight: 500 }}>
                  Възникна грешка
                </Typography>
                <Typography variant="body2">{error}</Typography>
              </Alert>
            )}

            {/* Форма */}
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ width: "100%" }}
            >
              <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
    
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    label="Вашето име"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    error={!!errors.name}
                    helperText={errors.name}
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person color={errors.name ? "error" : "action"} />
                        </InputAdornment>
                      ),
                    }}
                    disabled={loading}
                    sx={{ width: '100%' }}
                  />
                </Box>
                
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    label="Имейл адрес"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    error={!!errors.email}
                    helperText={errors.email}
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email color={errors.email ? "error" : "action"} />
                        </InputAdornment>
                      ),
                    }}
                    disabled={loading}
                    sx={{ width: '100%' }}
                  />
                </Box>
              </Box>

              {/* Втори ред: Тема и Тип обратна връзка */}
              <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    label="Тема"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    error={!!errors.subject}
                    helperText={errors.subject}
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Subject color={errors.subject ? "error" : "action"} />
                        </InputAdornment>
                      ),
                    }}
                    disabled={loading}
                    sx={{ width: '100%' }}
                  />
                </Box>
                
                <Box sx={{ flex: 1 }}>
                  <FormControl 
                    fullWidth 
                    error={!!errors.feedbackType}
                  >
                    <InputLabel>Тип обратна връзка</InputLabel>
                    <Select
                      name="feedbackType"
                      value={formData.feedbackType}
                      onChange={handleChange}
                      label="Тип обратна връзка"
                      disabled={loading}
                      sx={{ width: '100%' }}
                    >
                      {feedbackTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>{type.icon}</span>
                            <span>{type.label}</span>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.feedbackType && (
                      <FormHelperText>{errors.feedbackType}</FormHelperText>
                    )}
                  </FormControl>
                </Box>
              </Box>

              {/* Трети ред: Съобщение - разпънато */}
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  label="Вашето съобщение"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  error={!!errors.message}
                  helperText={errors.message}
                  variant="outlined"
                  multiline
                  rows={8}
                  placeholder="Моля, опишете подробно вашата забележка, въпрос или предложение..."
                  disabled={loading}
                  sx={{ 
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                      minHeight: '180px'
                    }
                  }}
                />
              </Box>

              {/* Препоръки */}
              <Box sx={{ mb: 3 }}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2.5,
                    backgroundColor: "rgba(0, 0, 0, 0.02)",
                    borderRadius: 2,
                    width: "100%",
                  }}
                >
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    💡 Препоръки за ефективна обратна връзка:
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.6 }}
                  >
                    • Бъдете конкретни и предоставете релевантна информация
                    <br />
                    • Посочете конкретна община, читалище или индикатор
                    <br />
                    • Опишете вида на грешката или непълнотата
                    <br />• При възможност, приложете снимка или допълнителни
                    източници
                  </Typography>
                </Paper>
              </Box>

              {/* Бутони */}
              <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', mt: 4 }}>
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  disabled={loading}
                  sx={{ 
                    px: 6, 
                    py: 1.2,
                    borderRadius: 2,
                    fontWeight: 500,
                    minWidth: 180
                  }}
                >
                  Изчисти формата
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Send />}
                  disabled={loading}
                  sx={{ 
                    px: 6, 
                    py: 1.2,
                    borderRadius: 2,
                    fontWeight: 500,
                    minWidth: 180
                  }}
                >
                  {loading ? 'Изпращане...' : 'Изпрати'}
                </Button>
              </Box>
            </Box>

            {/* Допълнителна информация */}
            <Box sx={{ mt: 4, width: "100%" }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  backgroundColor: "rgba(25, 118, 210, 0.04)",
                  borderRadius: 2,
                  width: "100%",
                }}
              >
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{ fontWeight: 600, color: "primary.main" }}
                >
                  🔒 Поверителност и обработка на данни
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ lineHeight: 1.7, textAlign: "justify" }}
                >
                  Вашата лична информация ще бъде използвана само за обработка
                  на вашето запитване и няма да бъде споделяна с трети страни.
                  Всички сигнали се обработват с поверителност и се използват
                  само за целите на подобряване на Общинския барометър на
                  читалищата.
                </Typography>
              </Paper>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ContactPage;
